import { Controller } from "@hotwired/stimulus"

// Sorts the rows of the coder review items table in place, without reloading the page (which
// would drop the applied filters and refetch the document viewer). The rows carry what is needed
// as data-filter-* attributes (ClaimsHelper#bill_item_filter_data). Only used on a flat table: a
// bill with nested lines keeps its parents in bill order.
//
// A header's first click sorts in the direction it declares; clicking the active header again
// reverses it. Lines with no value go last either way, and ties stay in bill order.
const COLUMNS = {
  charges: { attribute: "filterCharges", numeric: true },
  date: { attribute: "filterServiceDate", numeric: false },
  bill: { attribute: "filterItemId", numeric: true }
}

export default class extends Controller {
  static targets = ["body", "header", "reset"]
  // The order the server rendered the rows in.
  static values = { column: { type: String, default: "bill" }, direction: { type: String, default: "asc" } }

  connect() {
    this.showState()
  }

  sort(event) {
    event.preventDefault()
    const { column, first } = event.params
    const direction = this.columnValue === column ? this.opposite(this.directionValue) : first
    this.apply(column, direction)
  }

  reset(event) {
    event.preventDefault()
    this.apply("bill", "asc")
  }

  apply(column, direction) {
    const rows = Array.from(this.bodyTarget.children)
    rows.sort((a, b) => this.compare(a, b, column, direction) || this.compare(a, b, "bill", "asc"))
    rows.forEach(row => this.bodyTarget.appendChild(row))
    this.columnValue = column
    this.directionValue = direction
    this.showState()
  }

  compare(a, b, column, direction) {
    const first = this.valueOf(a, column)
    const second = this.valueOf(b, column)
    if (first === null || second === null) return (first === null ? 1 : 0) - (second === null ? 1 : 0)
    if (first === second) return 0
    const ascending = first < second ? -1 : 1
    return direction === "asc" ? ascending : -ascending
  }

  valueOf(row, column) {
    const { attribute, numeric } = COLUMNS[column]
    const raw = row.dataset[attribute]
    if (raw === undefined || raw === "") return null
    if (!numeric) return raw
    const number = Number(raw)
    return Number.isNaN(number) ? null : number
  }

  opposite(direction) {
    return direction === "asc" ? "desc" : "asc"
  }

  // Marks the active header with its direction and offers "bill order" when it is not the order.
  showState() {
    this.headerTargets.forEach(header => {
      const active = header.dataset.billItemsSortColumnParam === this.columnValue
      header.classList.toggle("text-core-purple-600", active)
      const arrow = header.querySelector("[data-sort-arrow]")
      if (arrow) arrow.textContent = active ? (this.directionValue === "asc" ? " ↑" : " ↓") : ""
    })
    if (this.hasResetTarget) this.resetTarget.classList.toggle("hidden", this.columnValue === "bill")
  }
}
